/* eslint-disable complexity */
import { getDefinition } from 'app/support/searchParameterDefinition';

export function availableSearchParametersForSearch(
  completedSearch,
  searchProfile
) {
  const { family, editorialproducts } = completedSearch;
  const creativeonly = family === 'creative';
  const bothfamilies =
    !family || (family.includes('creative') && family.includes('editorial'));
  const creative = creativeonly || bothfamilies;
  const editorialonly = family === 'editorial';
  const editorialarchival = editorialonly && editorialproducts === 'archival';
  const editorial = editorialonly || bothfamilies;
  const videotemplate = completedSearch.assettype === 'videotemplate';
  const imageonly = completedSearch.assettype === 'image';
  const image = imageonly || completedSearch.assettype === 'image,film';
  const filmonly = completedSearch.assettype === 'film';
  const artistexact = !!completedSearch.artistexact;
  const portfolio = !!completedSearch.contributor;
  const events = !!completedSearch.events;
  const morelikethis = !!completedSearch.morelikethis;
  const curatedset = !!completedSearch.curatedset;
  const searchbyimage = !!completedSearch.searchbyimage;
  const istocksite = searchProfile.site.nickname === 'istock';
  const gettysite = searchProfile.site.nickname === 'getty';
  const availableParams = {};

  const addExcludedValuesToDefinition = function addExcludedValuesToDefinition(
    parameterDefinition,
    excludedValues
  ) {
    const newParameterDefinition = { ...parameterDefinition };
    if (!newParameterDefinition.excludedValues) {
      newParameterDefinition.excludedValues = [];
    }
    for (const excludedValue of excludedValues) {
      if (newParameterDefinition.excludedValues.indexOf(excludedValue) < 0) {
        newParameterDefinition.excludedValues.push(excludedValue);
      }
    }
    return newParameterDefinition;
  };

  const addParam = (parameterName) => {
    let paramDefinition = getDefinition(parameterName);
    switch (paramDefinition.name) {
      case 'sortorder':
        if (creativeonly) {
          paramDefinition = addExcludedValuesToDefinition(paramDefinition, [
            'oldest',
          ]);
        }

        if (editorialarchival) {
          paramDefinition = addExcludedValuesToDefinition(paramDefinition, [
            'best',
          ]);
        }

        if (videotemplate) {
          paramDefinition = addExcludedValuesToDefinition(paramDefinition, [
            'mostpopular',
            'newest',
            'oldest',
          ]);
        }

        break;
      case 'license':
        if (imageonly) {
          paramDefinition = addExcludedValuesToDefinition(paramDefinition, [
            'rr',
            'rf,rr',
          ]);
        }
        if (filmonly) {
          paramDefinition = addExcludedValuesToDefinition(paramDefinition, [
            'rm',
            'rf,rm',
          ]);
        }
        break;
      default:
        break;
    }
    availableParams[parameterName] = paramDefinition;
  };

  const addParams = (params) => {
    params.forEach((param) => {
      addParam(param);
    });
  };

  addParams([
    'assettype',
    'artistexact',
    'family',
    'page',
    'phrase',
    'morelikethis',
    'recentlyviewed',
    'searchbyimage',
    'stackid',
    'suppressphrasecorrection',
    'orientations',
  ]);

  if (image || filmonly) {
    addParams(['locations']);
  }

  if (videotemplate) {
    addParams(['videotemplatecategory', 'videotemplateappversion']);
  }
  if (searchbyimage) {
    addParams(['imageurl']);
  }
  if (!morelikethis && !curatedset && !searchbyimage) {
    addParams(['sortorder']); // ~sort querystring value
  }
  if (image && creativeonly) {
    addParams(['assetfiletype']); // asset sub-type/file format, like EPS for the illustrations mediatype.
    addParams(['alloweduse', 'bwonly', '_colorindex', 'phraseprocessing']);
  }
  if (image && editorialonly) {
    addParams(['phraseprocessing']);
  }
  if (image) {
    addParams(['imagesize']);
  }
  if (image && creative) {
    addParams(['mediatype']);
  }
  if (
    image &&
    creativeonly &&
    !morelikethis &&
    !curatedset &&
    !searchbyimage &&
    !artistexact &&
    !portfolio
  ) {
    addParams(['mood']);
  }
  if (filmonly) {
    addParams(['videodurationmin', 'videodurationmax']);
    addParams(['filmdefinition', 'framerate']);
  }
  if (creative && (image || filmonly)) {
    addParams(['ethnicity', 'ageofpeople']);
  }

  if (gettysite) {
    addParams([
      'agreements',
      'collections',
      'curatedset',
      'easyaccess',
      'editorialsubscription',
      'premiumaccess',
      'royaltyfreesubscription',
      'compositions',
      'subPreview',
      'subPreviewSku',
    ]);

    if (image || filmonly) {
      addParams(['people']);
    }
    if (image) {
      addParams(['embeddable']);
    }
    if (creativeonly && (image || filmonly)) {
      addParams(['excludenudity']);
    }
    if (creative && (image || filmonly)) {
      addParams(['suppressfamilycorrection']);
    }
    if (imageonly && creative) {
      addParams(['specialcollectionid']);
    }
    if (imageonly && creativeonly) {
      addParams(['clarifications']);
    }
    if (image && editorial) {
      addParams(['editorialsources']); // TODO: hook up editorialsources param to redux.
    }
    if (filmonly && creative) {
      addParams(['releasestatus', 'phraseprocessing']);
    }
    if (filmonly) {
      addParams(['imagetechnique', 'viewpoint']);
    }
    if (filmonly && editorial) {
      addParams(['offlinecontent']);
    }
    if (editorial) {
      addParams([
        'begindate',
        'enddate',
        'recency',
        'eventid',
        'parenteventid',
      ]);
    }
    if (editorial && !morelikethis) {
      addParams(['events']);
    }

    // editorialproducts param required for SRP and editorial landing pages
    if (editorialonly) {
      addParams(['editorialproducts', 'specificpeople']);
    }
    if (editorialonly && !events && !morelikethis && !artistexact) {
      addParams(['groupbyevent']);
    }
    if (
      ((imageonly && bothfamilies) || (filmonly && !editorialonly)) &&
      !curatedset
    ) {
      addParams(['license']); // even though this may have commas it is still considered a single value param from our point of view
    }
    if (image || (filmonly && creative)) {
      addParams(['numberofpeople']);
    }
  }
  if (istocksite) {
    addParams(['excludenudity']);
    if (image || filmonly) {
      addParams(['istockcollection']);
      // even though this may have commas it is still considered a single value param from our point of view
      addParams(['contributor', 'istockexclusive', 'recency']);
      addParams(['numberofpeople']);
    }

    if (image) {
      addParams(['alloweduse']);
    }

    if (filmonly) {
      addParams(['phraseprocessing']);
    }
  }

  return availableParams;
}

// TOOD: Rename this function to getAvailableParameters and have it call availableSearchParametersForSearch,
// which will then become private to this module.
export function enforceAvailabilityForParameters(
  availableParams,
  currentParams
) {
  const newCurrentParams = { ...currentParams };
  for (const paramName in newCurrentParams) {
    if (!availableParams[paramName]) {
      delete newCurrentParams[paramName];
    }
  }
  return newCurrentParams;
}

export function isSearchParameterAvailable(
  availableSearchParameters,
  searchParameter
) {
  return Object.keys(availableSearchParameters).includes(searchParameter);
}

export function isAvailableParameterValue(
  availableSearchParameters,
  parameterName,
  value
) {
  if (!availableSearchParameters[parameterName]) {
    return false;
  }

  const { excludedValues } = availableSearchParameters[parameterName];
  if (excludedValues && excludedValues.indexOf(value) > -1) {
    return false;
  }

  return true;
}

export function isVisibleParameter(
  availableSearchParameters,
  parameterName,
  gallery
) {
  const agreementParams = [
    'premiumaccess',
    'easyaccess',
    'editorialsubscription',
    'royaltyfreesubscription',
  ];
  if (!agreementParams.includes(parameterName) && gallery.isAssetIdSearch()) {
    return false;
  }

  return isSearchParameterAvailable(availableSearchParameters, parameterName);
}
